import {Select} from '@wandb/weave/components/Form/Select';
import {TextField} from '@wandb/weave/components/Form/TextField';
import React from 'react';

import {ErrorText} from '../../shared';
import {
  comparisonOperatorOptions,
  statusAggregationOptions,
} from '../constants';
import {
  type AggregationFunction,
  type AggregationFunctionOption,
  type ComparisonOperator,
  type ComparisonOperatorOption,
} from '../types';

type AggregationThresholdFieldsProps = {
  aggregationFunction: AggregationFunction;
  threshold: string;
  operator: ComparisonOperator;
  onAggregationFunctionChange: (value: AggregationFunction) => void;
  onThresholdChange: (value: string) => void;
  onOperatorChange: (operator: ComparisonOperator) => void;
  hasThresholdError: boolean;
  aggregationOptions?: AggregationFunctionOption[];
};

/**
 * Percentage/count aggregation mode + operator/threshold rows shared by the
 * calls status config and the spans error-rate config. The only difference
 * between the two is the aggregation option labels ("calls" vs "spans").
 */
export const AggregationThresholdFields = ({
  aggregationFunction,
  threshold,
  operator,
  onAggregationFunctionChange,
  onThresholdChange,
  onOperatorChange,
  hasThresholdError,
  aggregationOptions = statusAggregationOptions,
}: AggregationThresholdFieldsProps) => {
  const currentAggOption =
    aggregationOptions.find(opt => opt.value === aggregationFunction) ??
    aggregationOptions[0];
  const currentOperatorOption =
    comparisonOperatorOptions.find(option => option.value === operator) ?? null;

  const isPercentage = aggregationFunction === 'percentage';

  return (
    <>
      {/* Aggregation mode */}
      <div>
        <div className="mb-4 text-sm text-moon-600 dark:text-wb-text-secondary">
          Alert when
        </div>
        <Select<AggregationFunctionOption>
          value={currentAggOption}
          onChange={option => {
            const newAgg = option?.value || 'percentage';
            onAggregationFunctionChange(newAgg);
            // Reset threshold to sensible default when switching modes
            // to avoid carrying over e.g. "10" (from 10%) as "10 calls"
            if (
              newAgg === 'percentage' &&
              aggregationFunction !== 'percentage'
            ) {
              onThresholdChange('10');
            } else if (newAgg === 'count' && aggregationFunction !== 'count') {
              onThresholdChange('1');
            }
          }}
          options={aggregationOptions}
        />
      </div>

      {/* Threshold comparison */}
      <div>
        <div className="mb-4 text-sm text-moon-600 dark:text-wb-text-secondary">
          Is
        </div>
        <div className="flex flex-row gap-8">
          <div className="w-full">
            <Select<ComparisonOperatorOption>
              value={currentOperatorOption}
              onChange={e => onOperatorChange(e?.value || 'GREATER_THAN')}
              options={comparisonOperatorOptions}
            />
          </div>
          <div className="w-full">
            <TextField
              ariaLabel="Threshold"
              value={threshold}
              onChange={onThresholdChange}
              placeholder={isPercentage ? 'Ex. 10' : 'Ex. 5'}
            />
          </div>
          {isPercentage && (
            <span className="flex items-center text-sm text-moon-600 dark:text-wb-text-secondary">
              %
            </span>
          )}
        </div>
        {hasThresholdError && (
          <ErrorText>
            {isPercentage
              ? 'Percentage must be a number between 0 and 100'
              : 'Count must be a valid positive number'}
          </ErrorText>
        )}
      </div>
    </>
  );
};
